import { useEffect, useRef, useState } from "react";

import { Link } from "react-router";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { PlaybookSvg } from "@/components/pieces/icons/svg/playbook";
import { BRAND_PURPLE, CARD_CLASSES, MAINTEXT_COLOR } from "@/css/constants";
import { FullMeeting } from "@/types/meetings";

interface ClampedTextWithToggleProps {
  text: string;
  lines?: number;
  className?: string;
}

export function ClampedTextWithToggle({
  text,
  lines = 3,
  className,
}: ClampedTextWithToggleProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      // Force reflow to ensure styles applied
      window.getComputedStyle(el).lineHeight; // just to ensure styles are applied
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  }, [text]);

  function onSeeMore(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setShowAll(!showAll);
  }

  return (
    <div>
      <Box
        className={!showAll ? `line-clamp-${lines}` : ""}
        ref={textRef}
        fontSize={16}
        fontWeight={400}
      >
        <RichContentRenderer className={className} content={text} />
      </Box>
      {isClamped && !showAll && (
        <a
          onClick={onSeeMore}
          href="#"
          style={{ fontWeight: 400, fontSize: 16 }}
        >
          See more
        </a>
      )}
    </div>
  );
}

interface ComponentInfoProps {
  programComponent: FullMeeting["programComponent"];
}

export default function ComponentInfo({
  programComponent,
}: ComponentInfoProps) {
  if (!programComponent) {
    return null;
  }

  const {
    name: componentName,
    participantGuidance,
    program,
    id: componentId,
  } = programComponent;
  const { name: programName, id: programId } = program || {};

  return (
    <Box
      mb={4}
      backgroundColor="#FFF"
      className={CARD_CLASSES}
      color={MAINTEXT_COLOR}
      p={[3, 3, 24]}
    >
      <Box fontSize={18} fontWeight={500}>
        {componentName}
        <Flex mt={2} alignItems="center" fontSize={16} fontWeight={400}>
          <Link
            to={`/programs/${programId}#component_id_${componentId}`}
            className="flex items-center"
          >
            <PlaybookSvg size={16} color={BRAND_PURPLE} />
            <Box ml={2}> {programName}</Box>
          </Link>
        </Flex>
        {participantGuidance && (
          <>
            <Box className="bbs" my={3} />
            <div className="mt-2">
              <ClampedTextWithToggle text={participantGuidance} lines={3} />
            </div>
          </>
        )}
      </Box>
    </Box>
  );
}
